// UPost.com - Main App
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LandingPage } from './pages/LandingPage';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { Dashboard } from './pages/Dashboard';
import { Editor } from './pages/Editor';
import { Upgrade } from './pages/Upgrade';
import { AcceptInvite } from './pages/AcceptInvite';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { CreditSuccess } from './pages/CreditSuccess';
import { PaymentCancel } from './pages/PaymentCancel';
import { AdminDashboard } from './pages/AdminDashboard';
import { ReferralDashboard } from './pages/ReferralDashboard';
import AffiliateLanding from './pages/AffiliateLanding';
import AffiliateDashboard from './pages/AffiliateDashboard';
import AffiliateSimpleLanding from './pages/AffiliateSimpleLanding';
import AffiliatePortfolioLanding from './pages/AffiliatePortfolioLanding';
import AffiliatePromoPage from './pages/AffiliatePromoPage';
import AffiliateRecruitPage from './pages/AffiliateRecruitPage';
import AffiliateTerms from './pages/AffiliateTerms';
import AffiliateStatus from './pages/AffiliateStatus';
import AffiliatePasswordReset from './pages/AffiliatePasswordReset';
import { ScreenplayConversionLanding } from './pages/ScreenplayConversionLanding';
import { W9Form } from './pages/W9Form';
import { MediaKit } from './pages/MediaKit';
import { PricingComparison } from './pages/PricingComparison';
import { PricingUpdated } from './pages/PricingUpdated';
import { CopyrightProtection } from './pages/CopyrightProtection';
import { AmazonPublishingGuide } from './pages/AmazonPublishingGuide';
import { ScreenplayGuide } from './pages/ScreenplayGuide';
import { NYTBestsellerGuide } from './pages/NYTBestsellerGuide';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { PressReleases } from './pages/PressReleases';
import { PressRelease } from './pages/PressRelease';
import { ProfileSettings } from './pages/ProfileSettings';
import { LogoShowcase } from './components/LogoShowcase';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Cookies } from './pages/Cookies';
import { Support } from './pages/Support';
import { BookCoverDesigner } from './pages/BookCoverDesigner';
import { ABTestDashboard } from './pages/ABTestDashboard';
import { CitationDashboard } from './pages/CitationDashboard';
import ClubLanding from './pages/ClubLanding';
import ClubDashboard from './pages/ClubDashboard';
import ClubLeaderboard from './pages/ClubLeaderboard';
import GrowthHub from './pages/GrowthHub';
import { NotFound } from './pages/NotFound';
import { About } from './pages/About';
import { KindleResearch } from './pages/KindleResearch';
import { KDPSetupGuide } from './pages/KDPSetupGuide';
import { KindleABDashboard } from './pages/KindleABDashboard';
import { AIGhostwriterLanding } from './pages/AIGhostwriterLanding';
import { GhostwriterDashboard } from './pages/GhostwriterDashboard';
import { GhostwriterPricing } from './pages/GhostwriterPricing';
import { GhostwriterHowItWorks } from './pages/GhostwriterHowItWorks';
import { GhostwriterFAQ } from './pages/GhostwriterFAQ';
import { GhostwriterContact } from './pages/GhostwriterContact';
import { GhostwriterAbout } from './pages/GhostwriterAbout';
import { GhostwriterLayout } from './components/GhostwriterLayout';
import { AIChatWidget } from './components/AIChatWidget';
import { captureReferral } from './utils/referral';
import './index.css';

// Capture affiliate referral from ?ref= param on any page
captureReferral();

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Domain detection for aighostwriterpro.com
const isGhostwriterDomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'aighostwriterpro.com' || hostname === 'www.aighostwriterpro.com';
};

// WWW to non-WWW redirect (client-side)
if (window.location.hostname.startsWith('www.')) {
  const newUrl = window.location.href.replace('://www.', '://');
  window.location.replace(newUrl);
}

function AppRoutes() {
  const ghostwriterDomain = isGhostwriterDomain();
  
  // Wrapper for ghostwriter domain pages
  const GW = ({ children }) => ghostwriterDomain ? <GhostwriterLayout>{children}</GhostwriterLayout> : children;
  
  return (
    <Routes>
      <Route path="/" element={ghostwriterDomain ? <GhostwriterLayout><AIGhostwriterLanding /></GhostwriterLayout> : <LandingPage />} />
      
      {/* Ghostwriter domain pages */}
      <Route path="/pricing" element={ghostwriterDomain ? <GW><GhostwriterPricing /></GW> : <PricingUpdated />} />
      <Route path="/how-it-works" element={<GW><GhostwriterHowItWorks /></GW>} />
      <Route path="/faq" element={<GW><GhostwriterFAQ /></GW>} />
      <Route path="/contact" element={ghostwriterDomain ? <GW><GhostwriterContact /></GW> : <Support />} />
      <Route path="/about" element={ghostwriterDomain ? <GW><GhostwriterAbout /></GW> : <About />} />
      <Route path="/pricing" element={<PricingUpdated />} />
      <Route path="/pricing-old" element={<PricingComparison />} />
      <Route path="/copyright-protection" element={<CopyrightProtection />} />
      <Route path="/amazon-publishing" element={<AmazonPublishingGuide />} />
      <Route path="/screenplay-guide" element={<ScreenplayGuide />} />
      <Route path="/nyt-bestseller-guide" element={<NYTBestsellerGuide />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/press" element={<PressReleases />} />
      <Route path="/press/:id" element={<PressRelease />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/affiliates" element={<AffiliateLanding />} />
      <Route path="/affiliate" element={<AffiliateLanding />} />
      <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
      <Route path="/affiliate/simple" element={<AffiliateSimpleLanding />} />
      <Route path="/affiliate/portfolio" element={<AffiliatePortfolioLanding />} />
      <Route path="/affiliate/promo" element={<AffiliatePromoPage />} />
      <Route path="/affiliate/promo/:affiliateCode" element={<AffiliatePromoPage />} />
      <Route path="/affiliate/promo/:affiliateCode/:template" element={<AffiliatePromoPage />} />
      <Route path="/affiliate/recruit" element={<AffiliateRecruitPage />} />
      <Route path="/affiliate/recruit/:affiliateCode" element={<AffiliateRecruitPage />} />
      <Route path="/affiliate/recruit/:affiliateCode/:template" element={<AffiliateRecruitPage />} />
      <Route path="/affiliate/terms" element={<AffiliateTerms />} />
      <Route path="/affiliate/status" element={<AffiliateStatus />} />
      <Route path="/affiliate/reset-password" element={<AffiliatePasswordReset />} />
      <Route path="/screenplay-conversion" element={<ScreenplayConversionLanding />} />
      <Route path="/logo-showcase" element={<LogoShowcase />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/about" element={<About />} />
      <Route path="/support" element={<Support />} />
      <Route path="/ab-test-results" element={<ABTestDashboard />} />
      <Route path="/citations" element={<CitationDashboard />} />
      <Route path="/kindle-research" element={<KindleResearch />} />
      <Route path="/kdp-setup-guide" element={<KDPSetupGuide />} />
      <Route path="/kindle-ab-dashboard" element={<KindleABDashboard />} />
      <Route path="/club" element={<ClubLanding />} />
      <Route path="/club/dashboard" element={<ClubDashboard />} />
      <Route path="/club/leaderboard" element={<ClubLeaderboard />} />
      <Route path="/club/growth" element={<ProtectedRoute><GrowthHub /></ProtectedRoute>} />
      <Route path="/arc" element={<ClubLanding />} />
      <Route path="/arc/*" element={<ClubLanding />} />
      <Route path="/ghostwriter" element={<AIGhostwriterLanding />} />
      <Route path="/ghostwriter/dashboard" element={<GhostwriterDashboard />} />
      <Route path="/payment-cancelled" element={<PaymentCancel />} />
      <Route
        path="/book-cover/:documentId"
        element={
          <ProtectedRoute>
            <BookCoverDesigner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor/:id"
        element={
          <ProtectedRoute>
            <Editor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <Upgrade />
          </ProtectedRoute>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/credits/success" element={<CreditSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/w9-form"
        element={
          <ProtectedRoute>
            <W9Form />
          </ProtectedRoute>
        }
      />
      <Route
        path="/media-kit"
        element={
          <ProtectedRoute>
            <MediaKit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />
      
      {/* 404 Catch-all Route - Must be last */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <AIChatWidget />
      </AuthProvider>
    </Router>
  );
}

export default App;
